/**
 * Mitglieder einer Abteilung: Suche + Hinzufügen, Tabelle mit Rolle und
 * Abteilungen, Entfernen. Gespeichert wird jeweils die komplette Liste
 * (PUT members {admin_ids}).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, Input } from '../../../components/ui';
import { departmentsApi, type DepartmentDetail, type DepartmentMember } from '../../../lib/departments';
import { v1Admins, type V1Admin } from '../../../lib/v1';
import { getThemeColors } from '../../../lib/theme';
import { apiErrorMessage } from './shared';

interface Props {
  dark: boolean;
  dept: DepartmentDetail;
  onSaved: () => void | Promise<void>;
}

function roleName(m: DepartmentMember): string {
  if (!m.role) return '';
  return typeof m.role === 'string' ? m.role : m.role.name;
}

export function DepartmentMembersTab({ dark, dept, onSaved }: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [admins, setAdmins] = useState<V1Admin[]>([]);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    v1Admins.list().then(a => { if (alive) setAdmins(a); }).catch(() => { /* Suche bleibt leer */ });
    return () => { alive = false; };
  }, []);

  const memberIds = useMemo(() => dept.members.map(m => m.id), [dept.members]);

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return admins
      .filter(a => !memberIds.includes(a.id))
      .filter(a => `${a.vorname ?? ''} ${a.nachname ?? ''} ${a.email}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [admins, memberIds, search]);

  const replace = async (ids: number[]) => {
    setBusy(true);
    setError(null);
    try {
      await departmentsApi.setMembers(dept.id, ids);
      await onSaved();
    } catch (e) {
      setError(apiErrorMessage(e, tr('departments.members.save_error')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card dark={dark} style={{ padding: 20 }}>
      <div style={{ fontSize: 13, color: t.muted, marginBottom: 14 }}>
        {tr('departments.members.info', { name: dept.name })}
      </div>

      <div style={{ position: 'relative', maxWidth: 360, marginBottom: 16 }}>
        <Input
          dark={dark}
          value={search}
          aria-label={tr('departments.members.search')}
          placeholder={tr('departments.members.search_placeholder')}
          onChange={e => setSearch(e.target.value)}
        />
        {candidates.length > 0 && (
          <ul
            aria-label={tr('departments.members.search_results')}
            style={{
              listStyle: 'none', margin: '4px 0 0', padding: 4, position: 'absolute', left: 0, right: 0, zIndex: 5,
              background: t.card, border: `1px solid ${t.border}`, borderRadius: 10, boxShadow: '0 6px 18px rgba(16,24,40,0.12)',
            }}
          >
            {candidates.map(a => (
              <li key={a.id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => { setSearch(''); void replace([...memberIds, a.id]); }}
                  style={{
                    width: '100%', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer',
                    padding: '7px 10px', borderRadius: 8, fontSize: 13, color: t.text, fontFamily: 'Outfit, sans-serif',
                  }}
                >
                  {[a.vorname, a.nachname].filter(Boolean).join(' ') || a.email}
                  <span style={{ color: t.muted, marginLeft: 8, fontSize: 12 }}>{a.email}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && <div role="alert" style={{ color: '#b42318', fontSize: 13, marginBottom: 10 }}>{error}</div>}

      {dept.members.length === 0 ? (
        <div style={{ color: t.muted, fontSize: 13 }}>{tr('departments.members.empty')}</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${t.border}` }}>
              {(['user', 'role', 'departments'] as const).map(c => (
                <th key={c} style={{ textAlign: 'left', padding: '8px 10px', color: t.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {tr(`departments.members.col_${c}`)}
                </th>
              ))}
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {dept.members.map(m => (
              <tr key={m.id} data-testid={`member-${m.id}`} style={{ borderBottom: `1px solid ${t.border}` }}>
                <td style={{ padding: '9px 10px' }}>
                  <div style={{ color: t.heading, fontWeight: 500 }}>{m.name}</div>
                  <div style={{ color: t.muted, fontSize: 12 }}>{m.email}</div>
                </td>
                <td style={{ padding: '9px 10px' }}>
                  {roleName(m) && <Badge dark={dark}>{roleName(m)}</Badge>}
                </td>
                <td style={{ padding: '9px 10px' }}>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {m.departments.map(s => <Badge key={s} dark={dark} tone={s === dept.slug ? 'brand' : 'neutral'}>{s}</Badge>)}
                  </div>
                </td>
                <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                  <Button dark={dark} variant="secondary" size="sm" disabled={busy} onClick={() => void replace(memberIds.filter(id => id !== m.id))}>
                    {tr('departments.members.remove')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ fontSize: 12, color: t.muted, marginTop: 14 }}>{tr('departments.members.footer')}</div>
    </Card>
  );
}
